import React, { useMemo, useState } from 'react';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import {
  SPOTIFY_HEADER_COVER_BLUR,
  SPOTIFY_HEADER_ZONES,
  spotifyHeaderCrop,
  spotifyHeaderCssBlurPx,
  spotifyHeaderThemeSvg,
  spotifyHeaderWashSvg,
  type HeaderBox,
} from 'fym-shared/src/music24/spotifyHeader';
import { TypedMemo } from '../../../../common/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The ground of a preview: the blurred cover, a picture (photo, bank, AI) or the theme field. */
export type HeaderPreviewGround =
  | { kind: 'cover'; artworkUrl: string | undefined }
  | { kind: 'picture'; url: string; focus: { x: number; y: number } }
  | { kind: 'theme' };

/** The size of a file in file pixels. */
export interface FrameSize {
  width: number;
  height: number;
}

const svgUrl = (svg: string): string => `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;

/** True when the ground draws the blurred cover (a cover ground with a known artwork). Pure. */
export function groundShowsCover(ground: HeaderPreviewGround): boolean {
  return ground.kind === 'cover' && ground.artworkUrl !== undefined;
}

/** The ground that a frame draws: a cover with no artwork falls back to the theme field. Pure. */
export function drawnGroundKind(ground: HeaderPreviewGround): HeaderPreviewGround['kind'] {
  return groundShowsCover(ground) || ground.kind !== 'cover' ? ground.kind : 'theme';
}

/** A picture placed like the render places it (`spotifyHeaderCrop` with the subject box), once its size is known. */
const FramePicture: React.FC<{
  url: string;
  focus: { x: number; y: number };
  frame: FrameSize;
  subject: HeaderBox;
}> = ({ url, focus, frame, subject }) => {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const crop = useMemo(
    () => (natural === null ? null : spotifyHeaderCrop(natural, frame, focus, subject)),
    [focus, frame, natural, subject]
  );
  return (
    <img
      src={url}
      alt=""
      className={styles.picture}
      style={crop === null ? undefined : { left: -crop.left, top: -crop.top, width: crop.width, height: crop.height }}
      onLoad={(event) =>
        setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })
      }
    />
  );
};

interface FrameGroundProps {
  /** The file size; the parent draws at file pixels and scales. */
  frame: FrameSize;
  /** The box that the focus point of a picture goes to (the header subject box by default). */
  subject?: HeaderBox;
  ground: HeaderPreviewGround;
  colours: ThemeColours;
}

/**
 * The ground of a preview at file pixels, as the sharp render draws it (fym-shared
 * `spotifyHeader.ts`, music24-api `renderSpotifyHeader`): a picture cut with the focus point, the
 * blurred cover with the theme wash, or the theme field. The Spotify header and the artist Profile
 * images (`artistProfile.ts`) share it; each gives its own frame size and subject box.
 */
const FrameGroundComponent: React.FC<FrameGroundProps> = ({
  frame,
  subject = SPOTIFY_HEADER_ZONES.subject,
  ground,
  colours,
}) => {
  const washUrl = useMemo(() => svgUrl(spotifyHeaderWashSvg(colours, frame)), [colours, frame]);
  const themeUrl = useMemo(() => svgUrl(spotifyHeaderThemeSvg(colours, frame)), [colours, frame]);
  if (ground.kind === 'picture') {
    return <FramePicture key={ground.url} url={ground.url} focus={ground.focus} frame={frame} subject={subject} />;
  }
  if (ground.kind === 'cover' && ground.artworkUrl !== undefined) {
    return (
      <>
        <img
          src={ground.artworkUrl}
          alt=""
          className={styles.cover}
          style={{
            filter: `blur(${spotifyHeaderCssBlurPx(frame.width)}px) saturate(${SPOTIFY_HEADER_COVER_BLUR.saturation})`,
            transform: `scale(${SPOTIFY_HEADER_COVER_BLUR.zoom})`,
          }}
        />
        <div className={styles.layer} style={{ backgroundImage: washUrl }} />
      </>
    );
  }
  return <div className={styles.layer} style={{ backgroundImage: themeUrl }} />;
};

export const FrameGround = TypedMemo(FrameGroundComponent);
